import { ZodiacSign } from '@/api/consts'
import type { AstroChart } from '@/api/schema'
import { Col, Row } from '@/app/components/Flex/Flex'
import { NatalChart, type NatalChartProps } from '@/app/components/NatalChart/NatalChart'
import { Planet, type PlanetProps } from '@/app/components/Planet/Planet'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import type { FunctionalComponent } from 'preact'
import { useState } from 'preact/hooks'
import styles from './index.module.scss'

const chart: AstroChart = {
  'planets': {
    'Sun': [
      124.20391201012501,
    ],
    'Moon': [
      274.9961398266567,
    ],
    'Mercury': [
      116.1027821569798,
    ],
    'Venus': [
      81.99231248606145,
    ],
    'Mars': [
      5.4660334138072795,
    ],
    'Jupiter': [
      60.8377196547021,
    ],
    'Saturn': [
      266.84080795963735,
      266.84080795963735,
    ],
    'Uranus': [
      267.68801502671016,
      267.68801502671016,
    ],
    'Neptune': [
      278.1247946379556,
      278.1247946379556,
    ],
    'Pluto': [
      219.77283626170401,
    ],
    'Asc': [
      120.56278072847203,
    ],
    'NNode': [
      344.75622055387015,
    ],
    'Lilith': [
      158.22320561941515,
    ],
    'Chiron': [
      93.22215285803543,
    ],
  },
  'signs': {
    'Sun': ZodiacSign.LEO,
    'Moon': ZodiacSign.CAPRICORN,
    'Mercury': ZodiacSign.CANCER,
    'Venus': ZodiacSign.GEMINI,
    'Mars': ZodiacSign.ARIES,
    'Jupiter': ZodiacSign.GEMINI,
    'Saturn': ZodiacSign.SAGITTARIUS,
    'Uranus': ZodiacSign.SAGITTARIUS,
    'Neptune': ZodiacSign.CAPRICORN,
    'Pluto': ZodiacSign.SCORPIO,
    'Asc': ZodiacSign.LEO,
    'NNode': ZodiacSign.PISCES,
    'Lilith': ZodiacSign.VIRGO,
    'Chiron': ZodiacSign.CANCER,
  },
  'cusps': [
    120.56278072847203,
    134.75931003027935,
    153.20404152429197,
    179.87950727607927,
    220.03713508511376,
    266.91688885720214,
    300.56278072847203,
    314.7593100302794,
    333.20404152429194,
    359.8795072760793,
    40.03713508511377,
    86.91688885720212,
  ],
  'aspects': [
    {
      'planet1': 'Sun',
      'planet2': 'Mars',
      'aspect': 120,
      'orb': 1.26,
    },
    {
      'planet1': 'Moon',
      'planet2': 'Mars',
      'aspect': 90,
      'orb': 0.47,
    },
    {
      'planet1': 'Moon',
      'planet2': 'Neptune',
      'aspect': 0,
      'orb': 3.13,
    },
    {
      'planet1': 'Moon',
      'planet2': 'Chiron',
      'aspect': 180,
      'orb': 1.77,
    },
    {
      'planet1': 'Saturn',
      'planet2': 'Uranus',
      'aspect': 0,
      'orb': 0.85,
    },
    {
      'planet1': 'Neptune',
      'planet2': 'Lilith',
      'aspect': 120,
      'orb': 0.1,
    },
  ],
}

const sections: {
  name: string
  desc: string
  planets: PlanetProps['planet'][]
}[] = [{
  name: 'Внутренний стержень',
  desc: 'Солнце — главное в тебе: характер, внутренняя сила и то, как ты проявляешь себя.',
  planets: ['Sun'],
}, {
  name: 'Эмоции',
  desc: 'По Луне видно, что тебя успокаивает, что ранит и как ты проживаешь чувства.',
  planets: ['Moon'],
}, {
  name: 'Мышление',
  desc: 'Меркурий — то, как ты думаешь, говоришь, споришь и воспринимаешь мир.',
  planets: ['Mercury'],
}, {
  name: 'Как ты любишь',
  desc: 'Венера про то, что тебя притягивает, как ты любишь и чего ждёшь от отношений.',
  planets: ['Venus'],
}, {
  name: 'Амбиции',
  desc: 'Марс, Сатурн и 10 дом — про цели, напор, дисциплину и то, как ты добиваешься своего.',
  planets: ['Mars', 'Saturn'],
}, {
  name: 'Деньги',
  desc: 'Венера, Юпитер и 2 дом — про заработок, траты, ценности и твои финансовые возможности.',
  planets: ['Venus', 'Jupiter'],
}]

export const NatalSection: FunctionalComponent = () => {
  const [accents, setAccents] = useState<NatalChartProps['accents'] | null>(null)

  return (
    <UI.Wrapper>
      <UI.Holder maxWidth={600}>
        <Row
          gap={30}
          gapMobile={25}
        >
          <div class={styles.chart}>
            <NatalChart
              accentScale={1.5}
              aspectStrokeWidth={2}
              negativeAspectColor='rgb(219, 37, 55)'
              positiveAspectColor='rgb(55 165 7)'
              accents={accents}
              chart={chart}
            />
          </div>

          <Row
            gap={20}
            gapMobile={15}
          >
            <Text
              as='h2'
              fontSize={34}
              fontSizeMobile={24}
              lineHeight={1}
              fontWeight={500}
              textAlign='center'
            >
              Натальная карта
            </Text>

            <Text
              as='p'
              fontSize={20}
              fontSizeMobile={16}
              lineHeight={1.25}
              textAlign='center'
            >
              Раскроем все элементы твоей карты — от&nbsp;характера&nbsp;и&nbsp;эмоций до отношений, карьеры и денег
            </Text>
          </Row>

          <Row
            gap={20}
            gapMobile={15}
          >
            <div class={styles.grid}>
              {sections.map(section => (
                <UI.Panel
                  hover
                  class={styles.item}
                  onPointerEnter={() => {
                    setAccents(section.planets)
                  }}
                  onPointerLeave={() => {
                    setAccents(null)
                  }}
                  link='~/tools?redirect=~/tools/natal'
                >
                  <Row
                    gap={5}
                  >
                    <Col alignItems='center' justifyContent='space-between'>
                      <Text
                        as='h4'
                        fontSize={18}
                        fontSizeMobile={16}
                        lineHeight={1}
                        fontWeight={600}
                      >
                        {section.name}
                      </Text>

                      <Col>
                        {section.planets.map(planet => (
                          <Planet
                            size={24}
                            planet={planet}
                            class={styles.planet}
                          />
                        ))}
                      </Col>
                    </Col>

                    <Text
                      as='p'
                      fontSize={16}
                      fontSizeMobile={14}
                      lineHeight={1.25}
                      opacity={0.5}
                    >
                      {section.desc}
                    </Text>
                  </Row>
                </UI.Panel>
              ))}
            </div>

            <UI.Button
              block
              size='l'
              type='primary'
              link='~/tools?redirect=~/tools/natal'
            >
              Построить карту
            </UI.Button>
          </Row>
        </Row>
      </UI.Holder>
    </UI.Wrapper>
  )
}
